import { useState, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import debounce from 'lodash/debounce';
import moment from 'moment';
import {
	compareStrings,
	nullSafeGet,
	nullSafeGetOrElse,
	getObjectValues,
} from '../utils/DataAccessUtils';
import { ROLE_TYPES } from '../utils/DataConstants';
import { getContractorOrWorkOrderId } from '../utils/WorkOrderUtils';
import {
	appendWorkOrderNotesForBuyer,
	appendWorkOrderNotesForSupplier,
	appendContractorWorkOrderNotes,
	appendInternalNotesForBuyer,
	appendInternalNotesForSupplier,
} from '../thunks/work_orders_thunks';
import {
	fetchAllUsersForBuyers,
	fetchAllUsersForSuppliers,
	fetchAllContractorWOUsersForSuppliers,
	fetchInternalNotesSubscribersForSupplier,
} from '../thunks/work_orders_subscribers_thunks';
import { workOrderChatUploadCustomRequest } from '../thunks/file_thunks';
import { formatUserContact } from '../utils/DataFormatterUtils';

/**
 * Hook for sending notes to a work order.
 * Encapsulates thunk selection based on user type and contractor WO status.
 */
export const useNoteSender = (
	workOrder: any,
	channel?: 'client' | 'internal' | 'subcontractor'
) => {
	const dispatch = useDispatch();
	const currentUser = useSelector((state: any) => state.session.currentUser);

	const workOrderId = useMemo(() => getContractorOrWorkOrderId(workOrder), [workOrder]);

	const contractorWorkOrderId = useMemo(() => {
		if (channel !== 'subcontractor') return workOrderId;
		const subDetails = nullSafeGetOrElse('subContractedDetails', workOrder, []);
		const supplierFacilityId = workOrder.currentUserSupplierFacilityId;
		const match = subDetails.find(
			(_) => _.subContractedFromSupplierFacilityId == supplierFacilityId
		);
		return nullSafeGet('id', match) || workOrderId;
	}, [workOrder, workOrderId, channel]);

	const sendNote = useCallback(
		(entity: any) => {
			let appendThunk;
			let noteId = workOrderId;
			if (channel === 'internal') {
				appendThunk =
					currentUser.userType === ROLE_TYPES.SUPPLIER
						? appendInternalNotesForSupplier
						: appendInternalNotesForBuyer;
			} else if (channel === 'subcontractor') {
				appendThunk = appendContractorWorkOrderNotes(
					currentUser.userType === ROLE_TYPES.SUPPLIER ? ROLE_TYPES.SUPPLIER : ROLE_TYPES.BUYER
				);
				noteId = contractorWorkOrderId;
			} else {
				if (currentUser.userType === ROLE_TYPES.SUPPLIER) {
					appendThunk = nullSafeGet('contractorWo', workOrder)
						? appendContractorWorkOrderNotes(ROLE_TYPES.SUPPLIER)
						: appendWorkOrderNotesForSupplier;
				} else {
					appendThunk = appendWorkOrderNotesForBuyer;
				}
			}

			return (dispatch as any)(
				appendThunk({
					id: noteId,
					note: entity,
				})
			);
		},
		[dispatch, currentUser, workOrder, workOrderId, channel, contractorWorkOrderId]
	);

	// `noteId` is the entity the note actually appends to. For the subcontractor
	// channel that's the contractor WO (a different ID than the parent), so
	// callers wiring up the WS broadcast must use this — not `workOrderId` —
	// otherwise contractor-notes listeners on the detail page won't match the
	// new-note event's entityId.
	return { sendNote, currentUser, workOrderId, noteId: contractorWorkOrderId };
};

/**
 * Hook for debounced @mention user search.
 * Extracts the search logic from ChatInput so it can be reused.
 */
export const useMentionSearch = (
	workOrder: any,
	channel?: 'client' | 'internal' | 'subcontractor'
) => {
	const dispatch = useDispatch();
	const currentUser = useSelector((state: any) => state.session.currentUser);

	const workOrderId = useMemo(() => getContractorOrWorkOrderId(workOrder), [workOrder]);

	const loadUsers = useCallback(
		(key: string, cb: (results: any[]) => void) => {
			// Workaround: MentionsInput calls this every time. If space count > 1, skip API call.
			// https://github.com/signavio/react-mentions/issues/247#issuecomment-516529736
			const count = key.split(' ').length - 1;
			if (count < 2) {
				if (channel === 'internal') {
					if (currentUser.userType === ROLE_TYPES.SUPPLIER) {
						(dispatch as any)(fetchInternalNotesSubscribersForSupplier(key, workOrderId)()).then(
							(data: any) =>
								cb(
									getObjectValues(data)
										.map((el: any) => formatUserContact(el))
										.sort(
											(a: any, b: any) =>
												Number(a.status === 'inactive') - Number(b.status === 'inactive')
										)
								)
						);
					} else {
						console.warn(
							'useMentionSearch: internal-notes subscriber search is not available for buyer users; returning empty results.'
						);
						cb([]);
					}
				} else if (currentUser.userType === ROLE_TYPES.SUPPLIER) {
					const fetchThunk = nullSafeGet('contractorWo', workOrder)
						? fetchAllContractorWOUsersForSuppliers(key, nullSafeGet('contractorWo.id', workOrder))
						: fetchAllUsersForSuppliers(key, workOrderId);
					(dispatch as any)(fetchThunk()).then((data: any) =>
						cb(
							getObjectValues(data)
								.map((el: any) => formatUserContact(el))
								.sort(
									(a: any, b: any) =>
										Number(a.status === 'inactive') - Number(b.status === 'inactive')
								)
						)
					);
				} else {
					(dispatch as any)(fetchAllUsersForBuyers(key, workOrderId)()).then((data: any) =>
						cb(
							getObjectValues(data)
								.map((el: any) => formatUserContact(el))
								.sort(
									(a: any, b: any) =>
										Number(a.status === 'inactive') - Number(b.status === 'inactive')
								)
						)
					);
				}
			} else {
				cb([]);
			}
		},
		[currentUser.userType, dispatch, workOrder, workOrderId, channel]
	);

	const debouncedLoadUsers = useMemo(() => debounce(loadUsers, 250), [loadUsers]);

	const onSearch = useCallback(
		(search: string, cb: (results: any[]) => void) => {
			debouncedLoadUsers(search, cb);
		},
		[debouncedLoadUsers]
	);

	return { onSearch };
};

/**
 * Hook for managing media upload state (photo, video, audio).
 * Manages file lists, upload progress, preview modals, and recording state.
 */
export const useMediaUpload = (
	workOrderId: string,
	sendNote: (entity: any) => Promise<any>,
	currentUser: any
) => {
	const [photoFileList, setPhotoFileList] = useState<any[]>([]);
	const [photoLinks, setPhotoLinks] = useState<string[]>([]);
	const [photoPreviewModalVisible, setPhotoPreviewModalVisible] = useState(false);
	const [videoFileList, setVideoFileList] = useState<any[]>([]);
	const [videoLink, setVideoLink] = useState('');
	const [videoPreviewModalVisible, setVideoPreviewModalVisible] = useState(false);
	const [fileUploading, setFileUploading] = useState(false);
	const [uploadingFileCount, setUploadingFileCount] = useState(0);
	const [uploadProgress, setUploadProgress] = useState(0);
	const handlePhotoUploadChange = useCallback((info: any) => {
		setPhotoFileList(info.fileList);
		if (info.fileList.some((file: any) => compareStrings(file.status, 'uploading'))) {
			const activeFiles = info.fileList.filter(
				(file: any) => file.status === 'uploading' || file.status === 'done'
			);
			const totalFileCount = activeFiles.length;
			const totalProgress = activeFiles.reduce((sum: number, file: any) => {
				const progress = file.status === 'done' ? 100 : nullSafeGetOrElse('percent', file, 0);
				return sum + progress;
			}, 0);
			const averageProgress = totalFileCount > 0 ? Math.round(totalProgress / totalFileCount) : 0;

			setFileUploading(true);
			setUploadingFileCount(totalFileCount);
			setUploadProgress(averageProgress);
			return;
		}
		if (info.fileList.every((file: any) => compareStrings(file.status, 'done'))) {
			const links = info.fileList.map((file: any) => nullSafeGetOrElse('response.data', file, ''));
			setFileUploading(false);
			setUploadingFileCount(0);
			setUploadProgress(0);
			setPhotoLinks(links || []);
			setPhotoPreviewModalVisible(true);
			return;
		}
	}, []);

	const onPhotoSubmit = useCallback(() => {
		const entities = photoLinks.map((photoLink) => ({
			text: '',
			photo: photoLink,
			noteAddedBy: currentUser.email,
			noteAddedByContact: currentUser,
			noteAddedAt: moment.utc(),
		}));
		entities.forEach((entity) => sendNote(entity));
		setPhotoLinks([]);
		setPhotoPreviewModalVisible(false);
		setPhotoFileList([]);
	}, [photoLinks, currentUser, sendNote]);

	const onPhotoCancel = useCallback(() => {
		setPhotoLinks([]);
		setPhotoPreviewModalVisible(false);
		setPhotoFileList([]);
	}, []);

	const handleVideoUploadChange = useCallback((info: any) => {
		setVideoFileList(info.fileList);
		if (info.file.status === 'uploading') {
			const progress = nullSafeGetOrElse('file.percent', info, 0);
			setFileUploading(true);
			setUploadingFileCount(1);
			setUploadProgress(Math.round(progress));
			return;
		}
		if (info.file.status === 'done') {
			const link = nullSafeGetOrElse('file.response.data', info, '');
			setFileUploading(false);
			setUploadingFileCount(0);
			setUploadProgress(0);
			setVideoLink(link);
			setVideoPreviewModalVisible(true);
		}
	}, []);

	const onVideoSubmit = useCallback(() => {
		const entity = {
			text: '',
			video: videoLink,
			noteAddedBy: currentUser.email,
			noteAddedByContact: currentUser,
			noteAddedAt: moment.utc(),
		};
		sendNote(entity);
		setVideoLink('');
		setVideoPreviewModalVisible(false);
		setVideoFileList([]);
	}, [videoLink, currentUser, sendNote]);

	const onVideoCancel = useCallback(() => {
		setVideoLink('');
		setVideoPreviewModalVisible(false);
		setVideoFileList([]);
	}, []);

	const handleSendAudio = useCallback(
		(blob: Blob, file: File) => {
			setFileUploading(true);
			setUploadingFileCount(1);
			setUploadProgress(0);

			workOrderChatUploadCustomRequest({
				userType: currentUser.userType,
				workOrderId,
				file,
				onProgress: ({ percent }: any) => {
					setUploadProgress(percent);
				},
				onSuccess: ({ data: audioLink }: any) => {
					setFileUploading(false);
					setUploadingFileCount(0);
					setUploadProgress(0);

					const entity = {
						text: '',
						audio: audioLink,
						noteAddedBy: currentUser.email,
						noteAddedAt: moment.utc(),
						noteAddedByContact: currentUser,
					};
					sendNote(entity);
				},
				onError: () => {
					setFileUploading(false);
					setUploadingFileCount(0);
					setUploadProgress(0);
				},
				source: 'RowChatInput',
			});
		},
		[currentUser, workOrderId, sendNote]
	);

	return {
		// Photo state
		photoFileList,
		photoLinks,
		photoPreviewModalVisible,
		handlePhotoUploadChange,
		onPhotoSubmit,
		onPhotoCancel,
		// Video state
		videoFileList,
		videoLink,
		videoPreviewModalVisible,
		handleVideoUploadChange,
		onVideoSubmit,
		onVideoCancel,
		// Audio / recording
		handleSendAudio,
		// Upload progress
		fileUploading,
		uploadingFileCount,
		uploadProgress,
	};
};
